import type { UnitDetailsTabValue } from 'pages/organizationFlow/organizationUnitDetails/index.data';

export const UNIT_DETAIL_TAB_QUERY_KEY = 'tab';

export const DEFAULT_UNIT_DETAIL_TAB: UnitDetailsTabValue = 'employees';

export const UNIT_DETAIL_TAB_VALUES: readonly UnitDetailsTabValue[] = [
  'employees',
  'member-verification',
  'hsa-deduction',
  'hsa-reimbursement',
  'past-deductions',
  'banking-files',
] as const;

const LEGACY_UNIT_DETAIL_TAB_ALIASES: Readonly<Record<string, UnitDetailsTabValue>> = {
  'hsa-disbursement': 'hsa-reimbursement',
};

const isUnitDetailsTabValue = (value: string): value is UnitDetailsTabValue =>
  (UNIT_DETAIL_TAB_VALUES as readonly string[]).includes(value);

/**
 * Reads active tab from `?tab=` (preferred) or legacy bare keys (`?employees`, `?hsa-deduction`, …).
 */
export const parseUnitDetailTabFromSearch = (search: string): UnitDetailsTabValue | null => {
  const params = new URLSearchParams(search);
  const tabParam = params.get(UNIT_DETAIL_TAB_QUERY_KEY)?.trim();
  if (tabParam && isUnitDetailsTabValue(tabParam)) {
    return tabParam;
  }

  if (tabParam && LEGACY_UNIT_DETAIL_TAB_ALIASES[tabParam]) {
    return LEGACY_UNIT_DETAIL_TAB_ALIASES[tabParam];
  }

  for (const tab of UNIT_DETAIL_TAB_VALUES) {
    if (params.has(tab)) {
      return tab;
    }
  }

  for (const [legacyTab, canonicalTab] of Object.entries(LEGACY_UNIT_DETAIL_TAB_ALIASES)) {
    if (params.has(legacyTab)) {
      return canonicalTab;
    }
  }

  return null;
};

/** Canonical unit detail URL keeps only the active tab (e.g. `?tab=employees`). */
export const buildUnitDetailSearch = (tab: UnitDetailsTabValue): string =>
  `?${UNIT_DETAIL_TAB_QUERY_KEY}=${tab}`;

/** Updates `tab` and drops legacy org/unit query params from the URL. */
export const setUnitDetailTabInSearch = (_search: string, tab: UnitDetailsTabValue): string =>
  buildUnitDetailSearch(tab);
